import { Flame, Inbox, Mic, Plus, RadioTower, Sun } from "lucide-react";
import { clsx } from "clsx";
import { useRef } from "react";
import { useTaskStore } from "../stores/taskStore";
import { useUiStore } from "../stores/uiStore";
import { haptic } from "../lib/haptics";
import { FilterMode, TaskStats } from "../lib/tauri";

// 下部タブは「毎日ワンタップで開く先」だけを置く quick-access 層。
// 司令塔 / 今日 / 未完了 / 重要(high) の4つ＋中央FAB。
// ナレッジ・案件レーン・ゴール・インボックス・ゴミ箱はハンバーガー(MobileDrawer→Sidebar)から到達する。
// ナレッジは司令塔ビューの「今日のナレッジ」カードがモバイルでも常時見えるため、毎日の見える化は確保される。
type TabKey = FilterMode | "command";

const leftTabs: Array<{
  key: TabKey;
  label: string;
  icon: typeof Sun;
  count: keyof TaskStats | null;
}> = [
  { key: "command", label: "司令塔", icon: RadioTower, count: null },
  { key: "today", label: "今日", icon: Sun, count: "today" },
];

const rightTabs: Array<{
  key: TabKey;
  label: string;
  icon: typeof Sun;
  count: keyof TaskStats | null;
}> = [
  { key: "active", label: "未完了", icon: Inbox, count: "active" },
  { key: "high", label: "重要", icon: Flame, count: "high" },
];

export function BottomTabBar() {
  const { filter, stats, setFilter } = useTaskStore();
  const appView = useUiStore((s) => s.appView);
  const setAppView = useUiStore((s) => s.setAppView);
  const setEditorOpen = useUiStore((s) => s.setEditorOpen);
  const setVoiceOpen = useUiStore((s) => s.setVoiceOpen);
  const longPressTimer = useRef<number | null>(null);
  const didLongPress = useRef(false);

  const startLongPress = () => {
    didLongPress.current = false;
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = window.setTimeout(() => {
      didLongPress.current = true;
      haptic("medium");
      setVoiceOpen(true);
    }, 450) as unknown as number;
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleFabTap = () => {
    if (didLongPress.current) {
      didLongPress.current = false;
      return;
    }
    haptic("light");
    setEditorOpen(true);
  };

  const renderTab = ({ key, label, icon: Icon, count }: typeof leftTabs[number]) => {
    const active =
      key === "command" ? appView === "command" : appView === "tasks" && filter === key;
    return (
      <button
        key={key}
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => {
          if (key === "command") {
            setAppView("command");
          } else {
            setAppView("tasks");
            void setFilter(key as FilterMode);
          }
        }}
        className={clsx(
          "flex flex-1 flex-col items-center justify-center gap-0.5 transition",
          active ? "text-brand-600 dark:text-brand-300" : "text-muted dark:text-muted-dark"
        )}
      >
        <div className="relative">
          <Icon className="h-5 w-5" />
          {count && stats[count] > 0 && (
            <span
              className={clsx(
                "absolute -right-2 -top-1 min-w-[14px] rounded-full px-1 text-[9px] font-bold leading-[14px] text-white",
                active ? "bg-brand-500" : "bg-muted dark:bg-dim-dark"
              )}
            >
              {stats[count]}
            </span>
          )}
        </div>
        <span className="text-[10px] font-medium">{label}</span>
      </button>
    );
  };

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-stretch border-t border-border bg-card/95 backdrop-blur pb-[env(safe-area-inset-bottom)] dark:border-border-dark dark:bg-card-dark/95 md:hidden"
      role="tablist"
    >
      {leftTabs.map(renderTab)}

      {/* Center floating + FAB (tap = editor, long-press = voice) */}
      <div className="relative flex w-16 items-end justify-center">
        <button
          type="button"
          aria-label="新規タスク (長押しで音声入力)"
          onClick={handleFabTap}
          onPointerDown={startLongPress}
          onPointerUp={cancelLongPress}
          onPointerCancel={cancelLongPress}
          onPointerLeave={cancelLongPress}
          className="absolute -top-5 flex h-14 w-14 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg ring-4 ring-bg dark:ring-bg-dark active:scale-95 active:bg-brand-600"
        >
          <Plus className="h-7 w-7" strokeWidth={2.5} />
        </button>
        {/* Mic hint below FAB */}
        <button
          type="button"
          aria-label="音声入力"
          onClick={() => {
            haptic("medium");
            setVoiceOpen(true);
          }}
          className="absolute -bottom-1 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-muted/30 text-muted dark:text-muted-dark"
        >
          <Mic className="h-2.5 w-2.5" />
        </button>
      </div>

      {rightTabs.map(renderTab)}
    </nav>
  );
}
